import { Model } from '@vuex-orm/core';
import Section from '@/models/Section';
import Sectionable from '@/models/Sectionable';
import Date from '@/models/Date';
import Illustration from '@/models/Illustration';
import Image from '@/models/Image';
import Imageable from '@/models/Imageable';
import Project from '@/models/Project';
import Projectable from '@/models/Projectable';

export default class Selector extends Model {
  static entity = 'selectors';

  static state() {
    return {
      viewType: 'list',
    };
  }

  static fields() {
    return {
      id: this.uid(),
      name: this.attr(''),

      singleSection: this.boolean(true),

      section_ids: this.attr(null),
      overview_illustration_ids: this.attr(null),
      section_illustration_ids: this.attr(null),

      projects: this.attr(null),

      // View Settings
      showIllustration: this.boolean(true),
      listType: this.attr(null),
      cardImageSize: this.attr(null),

      showPriceOnSold: this.boolean(false),
      showPriceOnReserved: this.boolean(false),

      showColorOnSectionForSale: this.boolean(false),
      showColorOnResidenceForSale: this.boolean(false),

      colorLoader: this.attr(null),
      colorButtons: this.attr(null),
      colorButtonText: this.attr(null),
      colorForSale: this.attr(null),
      colorReserved: this.attr(null),
      colorSold: this.attr(null),
      colorTextColor: this.attr(null),

      // Options
      hasOptions: this.boolean(false),

      // Calculator
      hasCalculator: this.boolean(false),
      hasComunalPricing: this.boolean(false),

      interestRateIndividualSelect: this.boolean(false),
      interestRateIndividual: this.number(0),
      individualLoanDescription: this.attr(null),

      depositSize: this.number(0),
      depositDescription: this.attr(null),
      communalLoanDescription: this.attr(null),
      communalLoanYears: this.number(30),

      interestRateComunal: this.number(0),
      interestFee: this.number(0),
      interestRateComunalDescription: this.attr(null),

      interestRateComunalExtras: this.attr(null),

      installmentPeriods: this.attr(null),

      fixedMonthlyCosts: this.number(0),
      flexibleMonthlyCosts: this.number(0),
      monthlyCostsDescription: this.attr(null),

      taxDeductionCheck: this.boolean(false),
      taxDeductionValue: this.number(0),
      taxDeductionDescription: this.attr(null),

      // Contact Form
      hasContactForm: this.boolean(false),
      contactForm: this.attr(null),
      contactFormTitle: this.attr(null),
      contactFormLead: this.attr(null),
      contactFormConcent: this.attr(null),

      // Trackers
      hasGoogleAnalytics: this.boolean(false),
      googleAnalyticsTrackingCode: this.attr(null),
      hasFacebookPixel: this.boolean(false),

      // Relationships
      sections: this.morphToMany(
        Section,
        Sectionable,
        'section_id',
        'sectionable_id',
        'sectionable_type'
      ),
      date: this.morphOne(Date, 'dateable_id', 'dateable_type'),
      illustrations: this.hasMany(Illustration, 'selector_id'),
      images: this.morphToMany(
        Image,
        Imageable,
        'image_id',
        'imageable_id',
        'imageable_type'
      ),
      projects: this.morphToMany(
        Project,
        Projectable,
        'project_id',
        'projectable_id',
        'projectable_type'
      ),
    };
  }

  static transformer(data) {
    const selectors = [];
    data.data.forEach(item => {
      const selector = {};
      selector.id = item.id;
      selector.name = item.name;

      // Add date object to Selector object
      selector.date = {
        date: item.date,
        date_gmt: item.date_gmt,
        modified: item.modified,
        modified_gmt: item.modified_gmt,
      };

      if (item.project.length > 0) {
        selector.projects = [];
        item.project.forEach(project => {
          selector.projects.push({ id: project });
        });
      }

      if (item.acf.section_select === 'multiple') {
        selector.singleSection = false;

        if (item.acf.overview) {
          selector.overview_illustration_ids = [];
          selector.illustrations = [];

          item.acf.overview.forEach(item => {
            selector.illustrations.push({ id: item.illustration });
            selector.overview_illustration_ids.push(item.illustration);
          });
        }

        if (item.acf.sections) {
          selector.sections = [];
          selector.section_ids = [];
          selector.section_illustration_ids = {};
          item.acf.sections.forEach(section => {
            // Add sections array to selector.
            selector.section_ids.push(section.section.term_id);

            if (section.section_illustrations) {
              selector.section_illustration_ids[section.section.term_id] = [];
              section.section_illustrations.forEach(item => {
                selector.section_illustration_ids[section.section.term_id].push(
                  item.illustration
                );
              });
            }

            let availabilityId = null;
            if (section.availability) {
              availabilityId = section.availability.term_id;
            }

            let shapeItems = [];
            if (section.shapes) {
              section.shapes.forEach(shape => {
                shapeItems.push({
                  illustration: shape.illustration,
                  polygon: shape.polygon,
                  path: shape.path,
                  shapeable_id: section.section.term_id,
                  shapeable_type: 'sections',
                });
              });
            }

            selector.sections.push({
              id: section.section.term_id,
              shapes: shapeItems,
              availability: {
                id: availabilityId,
              },
              url: section.url,
            });
          });
        }
      } else {
        selector.singleSection = true;
        selector.overview_illustration_ids = [];
        selector.illustrations = [];
        if (item.acf.section_single) {
          item.acf.section_single.forEach(item => {
            selector.illustrations.push({ id: item.illustration });
            selector.overview_illustration_ids.push(item.illustration);
          });
        }
      }

      // View settings
      selector.showIllustration = item.acf.illustration_show_check;
      selector.listType = item.acf.list_view_check;
      selector.cardImageSize = item.acf.image_size;

      selector.showPriceOnSold = item.acf.reservedprice_select;
      selector.showPriceOnReserved = item.acf.soldprice_select;

      selector.showColorOnSectionForSale = item.acf.colorsection_select;
      selector.showColorOnResidenceForSale = item.acf.colorresidence_select;

      selector.colorLoader = item.acf.color_loader;
      selector.colorButtons = item.acf.color_buttons;
      selector.colorButtonText = item.acf.color_buttontext;
      selector.colorForSale = item.acf.color_forsale;
      selector.colorReserved = item.acf.color_reserved;
      selector.colorSold = item.acf.color_sold;
      selector.colorTextColor = item.acf.text_hovercolor;

      // Options
      selector.hasOptions = JSON.parse(item.acf.options_select.toLowerCase());

      // Calculator
      selector.hasCalculator = JSON.parse(
        item.acf.calculator_select.toLowerCase()
      );
      selector.hasComunalPricing = JSON.parse(
        item.acf.pricing_select.toLowerCase()
      );

      selector.interestRateIndividualSelect = JSON.parse(
        item.acf.interest_rate_individual_select.toLowerCase()
      );
      selector.interestRateIndividual = item.acf.interest_rate_individual;
      selector.individualLoanDescription = item.acf.individual_loan_description;

      selector.depositSize = item.acf.deposit_size;
      selector.depositDescription = item.acf.deposit_description;
      selector.communalLoanDescription = item.acf.communal_loan_description;
      selector.communalLoanYears = parseFloat(item.acf.communal_loan_years);

      selector.interestRateComunal = item.acf.interest_rate_communal;
      selector.interestFee = item.acf.interest_fee;
      selector.interestRateComunalDescription =
        item.acf.interest_rate_communal_description;

      selector.interestRateComunalExtras = item.acf.custom_interest_rates;

      selector.installmentPeriods = item.acf.installment_periods;

      selector.fixedMonthlyCosts = item.acf.fixed_monthly_costs;
      selector.flexibleMonthlyCosts = item.acf.flexible_monthly_costs;
      selector.monthlyCostsDescription = item.acf.monthly_costs_description;

      selector.taxDeductionCheck = JSON.parse(
        item.acf.tax_deduction_check.toLowerCase()
      );
      selector.taxDeductionValue = item.acf.tax_deduction_value;
      selector.taxDeductionDescription = item.acf.tax_deduction_description;

      // Contact Form
      selector.hasContactForm = JSON.parse(
        item.acf.contact_form_select.toLowerCase()
      );
      selector.contactForm = item.acf.contact_form;
      selector.contactFormTitle = item.acf.contact_form_title;
      selector.contactFormLead = item.acf.contact_form_lead;
      selector.contactFormConcent = item.acf.contact_form_consent;

      // Trackers
      selector.hasGoogleAnalytics = JSON.parse(item.acf.ga_check.toLowerCase());
      selector.googleAnalyticsTrackingCode = item.acf.ga_tracking_code;
      selector.hasFacebookPixel = JSON.parse(item.acf.fb_check.toLowerCase());

      selectors.push(selector);
    });
    return selectors;
  }

  static apiConfig = {
    dataTransformer: data => this.transformer(data),
  };

  static fetch(selectorId, baseURL) {
    let url = '/wp-json/wp/v2/selector?order=asc&per_page=100';
    if (selectorId) {
      url += '&id=' + selectorId;
    }
    return this.api().get(url, {
      baseURL: baseURL,
    });
  }
}
